/**
 * Cloud Resources - resources/services under Cloud Services
 * Separate from Endpoints which are physical IT/OT devices under Sites
 * 
 * TODO: Phase 2 - Optional Cloud Systems grouping
 * Cloud Service → (optional Cloud System) → Cloud Resources
 */

export type CloudResourceType = 
  // Compute
  | 'Virtual Machine'
  | 'Container Instance'
  | 'Kubernetes Cluster'
  | 'Serverless Function'
  | 'App Service'
  // Storage
  | 'Blob Storage'
  | 'File Storage'
  | 'Archive Storage'
  | 'Disk'
  // Database
  | 'SQL Database'
  | 'NoSQL Database'
  | 'Cache'
  | 'Data Warehouse'
  // Networking
  | 'Virtual Network'
  | 'Load Balancer'
  | 'VPN Gateway'
  | 'CDN'
  | 'DNS Zone'
  // Security
  | 'Key Vault'
  | 'WAF'
  | 'Identity Service'
  // Application
  | 'API Gateway'
  | 'Message Queue'
  | 'IoT Hub'
  | 'Event Hub'
  // Other
  | 'Other';

export type CloudResourceStatus = 'active' | 'stopped' | 'deallocated' | 'deleted';
export type CloudResourceEnvironment = 'Production' | 'Staging' | 'Development' | 'Testing';
export type CloudResourceCriticality = 'Very Low' | 'Low' | 'Medium' | 'High' | 'Very High';

export interface CloudResource {
  id: string;
  org_id: string;
  cloud_service_id: string;
  
  // Resource identification
  name: string;
  resource_type: string;
  resource_id?: string; // Azure Resource ID, AWS ARN, GCP resource path
  
  // Cloud-specific properties
  sku?: string;
  region?: string;
  environment?: CloudResourceEnvironment;
  status?: CloudResourceStatus;
  criticality?: CloudResourceCriticality;
  owner_id?: string;
  
  // Metadata
  tags?: string[];
  notes?: string;
  metadata?: Record<string, unknown>;
  
  // Position for System Layout
  position_x?: number;
  position_y?: number;
  
  // Audit fields
  created_at: string;
  updated_at: string;
  created_by?: string;
  updated_by?: string;
}

export type CloudResourceInsert = Omit<CloudResource, 'id' | 'created_at' | 'updated_at'> & {
  id?: string;
};

export type CloudResourceUpdate = Partial<
  Omit<CloudResource, 'id' | 'org_id' | 'created_at' | 'updated_at'>
>;

// Categorized resource types for UI dropdowns
export const CLOUD_RESOURCE_CATEGORIES = {
  'Compute': ['Virtual Machine', 'Container Instance', 'Kubernetes Cluster', 'Serverless Function', 'App Service'],
  'Storage': ['Blob Storage', 'File Storage', 'Archive Storage', 'Disk'],
  'Database': ['SQL Database', 'NoSQL Database', 'Cache', 'Data Warehouse'],
  'Networking': ['Virtual Network', 'Load Balancer', 'VPN Gateway', 'CDN', 'DNS Zone'],
  'Security': ['Key Vault', 'WAF', 'Identity Service'],
  'Application': ['API Gateway', 'Message Queue', 'IoT Hub', 'Event Hub'],
  'Other': ['Other']
} as const;

export const CLOUD_RESOURCE_STATUSES: CloudResourceStatus[] = ['active', 'stopped', 'deallocated', 'deleted'];
export const CLOUD_RESOURCE_ENVIRONMENTS: CloudResourceEnvironment[] = ['Production', 'Staging', 'Development', 'Testing'];
export const CLOUD_RESOURCE_CRITICALITIES: CloudResourceCriticality[] = ['Very Low', 'Low', 'Medium', 'High', 'Very High'];
